import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Table, InputNumber, Collapse } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import FilterSelect from 'components/Form/filterSelect';
import DateField from 'components/Form/dateField';
import EssFullWidthModal from 'components/modelpopup/baseModal';
import { useUnsavedChangesGuard } from 'context/unsavedChangesContext';
import { createStockTransferApi, getWarehouseStockApi, uniformErrorMessage } from 'api/services/uniformManagement';
import { useUniformMgmt } from '../../context/uniformMgmtContext';
import DocumentAttachment from '../../components/DocumentAttachment';
import useStickyIdentity from '../../components/useStickyIdentity';

const toastOpts = { position: 'top-right', autoClose: 3000, transition: Bounce };

/** Unique row key for a warehouse-stock row: pairs uniform_item with size (or "none"
 *  for sizeless items). Matches the shape the backend's `errors[]` rows key on, so a
 *  400 response can be mapped straight back onto the grid. */
const rowKey = (row) => `${row.uniform_item}-${row.size ?? 'none'}`;

/** Create-only multi-line stock transfer modal (see spec §15). Fetches the source
 *  warehouse's in-stock rows into a grid; the user types a per-row transfer quantity
 *  capped at what's available, and every row carrying a quantity is submitted as one
 *  transfer with multiple lines. Rows left blank are simply not transferred. */
const TransferFormModal = ({ open, onClose, onCreated }) => {
  const { warehouseOptions, mainWarehouseOptions, warehousesLoading, warehousesError, fetchWarehouses } = useUniformMgmt();

  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [transferDate, setTransferDate] = useState('');
  const [notes, setNotes] = useState('');

  const [poNumber, setPoNumber] = useState('');
  const [poDate, setPoDate] = useState('');
  const [poDoc, setPoDoc] = useState(null);

  const [stockRows, setStockRows] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [search, setSearch] = useState('');
  // A row is part of the transfer purely by having a quantity typed into it —
  // there is no separate tick step. Anything left blank or zero is not sent.
  const [qtyMap, setQtyMap] = useState({});
  const [rowErrors, setRowErrors] = useState({});

  const [submitting, setSubmitting] = useState(false);

  // Kept in sync with qtyMap so the post-400 reconcile below (which runs
  // inside a fetchStock().then() callback, not a React updater) can read the latest
  // quantities without capturing a stale closure over the state at submit time.
  const qtyMapRef = useRef({});
  useEffect(() => { qtyMapRef.current = qtyMap; }, [qtyMap]);

  // "Latest request wins" guard for fetchStock below, matching the pattern
  // BulkIssuance/IssuanceFormModal already use for their warehouse-scoped
  // stock fetches — this was the one fetch in the module without one.
  const stockReqIdRef = useRef(0);

  // Reset the whole form whenever the modal is (re)opened.
  useEffect(() => {
    if (!open) return;
    setFrom(null);
    setTo(null);
    setTransferDate('');
    setNotes('');
    setPoNumber('');
    setPoDate('');
    setPoDoc(null);
    setStockRows([]);
    setSearch('');
    setQtyMap({});
    setRowErrors({});
  }, [open]);

  // Routes every `from` change through one place so the source's derived
  // state (stock rows, search, quantities, row errors) is always cleared on
  // a switch — not just when cleared to null. Without this, a slow or failed
  // fetch for the new source left the previous source's rows fully
  // interactive under a heading that already read the new warehouse.
  const handleFromChange = useCallback((val) => {
    setFrom(val);
    setTo((prevTo) => (prevTo && val && prevTo.value === val.value ? null : prevTo));
    // Invalidate any stock fetch already in flight for the source being left
    // — this is the guard's only reliable choke point. fetchStock's own
    // early-return bump (`if (!from) { stockReqIdRef.current += 1; ... }`)
    // is never reached on the clear path, since the effect that calls
    // fetchStock short-circuits on `!from` before fetchStock runs at all —
    // so without bumping here, a late response for a just-cleared source
    // could still land and repopulate `stockRows`, invisibly "safe" only
    // because the grid happens to be hidden while `from` is null.
    stockReqIdRef.current += 1;
    setStockRows([]);
    setSearch('');
    setQtyMap({});
    setRowErrors({});
  }, []);

  // Auto-pick the source (main) warehouse when there's only one active main
  // warehouse to choose from. Runs after the reset effect above (which
  // clears `from` to null on every open), so a single-option company never
  // makes the user pick. Also reconciles the other direction: if the picked
  // source drops out of the list (deactivated / stripped of the role
  // elsewhere), clear it rather than leave a now-invalid source selected.
  useEffect(() => {
    if (!open) return;
    if (mainWarehouseOptions.length === 1) {
      if (!from || from.value !== mainWarehouseOptions[0].value) {
        handleFromChange(mainWarehouseOptions[0]);
      }
    } else if (from && !mainWarehouseOptions.some((o) => o.value === from.value)) {
      handleFromChange(null);
    }
  }, [open, mainWarehouseOptions, from, handleFromChange]);

  // Resolves with the fetched rows (or [] on failure) so callers — notably the 400
  // reconcile path in handleSubmit's catch — can act on exactly what came back
  // instead of reading the `stockRows` state, which wouldn't have committed yet.
  // Guarded by stockReqIdRef ("latest request wins") so a slow response for a
  // source the user has since switched away from can't land later and
  // repopulate `stockRows` with another warehouse's rows.
  const fetchStock = useCallback(() => {
    if (!from) {
      // Bump the ref even on this early return so a still-in-flight response
      // for a since-cleared source is invalidated the same way.
      stockReqIdRef.current += 1;
      setStockRows([]);
      return Promise.resolve([]);
    }
    const reqId = ++stockReqIdRef.current;
    setStockLoading(true);
    return getWarehouseStockApi(from.value, { in_stock: true, page_size: 1000 }).then((response) => {
      const rows = response.data?.results || [];
      if (reqId === stockReqIdRef.current) {
        setStockRows(rows);
        setStockLoading(false);
      }
      return rows;
    }).catch((e) => {
      if (reqId === stockReqIdRef.current) {
        setStockRows([]);
        setStockLoading(false);
        toast.error(uniformErrorMessage(e, 'Failed to load source warehouse stock'), toastOpts);
      }
      return [];
    });
  }, [from]);

  useEffect(() => {
    if (!open || !from) return;
    fetchStock();
    // fetchStock is intentionally excluded: it already depends on `from`, and
    // including it here would refire this effect via its own identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, from]);

  const toOptions = useMemo(
    () => warehouseOptions.filter((w) => !from || w.value !== from.value),
    [warehouseOptions, from]
  );

  // Auto-pick the destination when the source leaves exactly one warehouse to
  // transfer to — the common single-main-warehouse, single-store company, where
  // making the user open a one-item dropdown is pure friction. Mirrors the
  // source effect above, and depends on `toOptions` (not `warehouseOptions`) so
  // it re-evaluates when the source changes and can never select the source
  // itself. Also reconciles the other direction: a destination that drops out
  // of the list — deactivated, or now the selected source — is cleared rather
  // than left selected and invalid.
  useEffect(() => {
    if (!open) return;
    if (toOptions.length === 1) {
      if (!to || to.value !== toOptions[0].value) {
        setTo(toOptions[0]);
      }
    } else if (to && !toOptions.some((o) => o.value === to.value)) {
      setTo(null);
    }
  }, [open, toOptions, to]);

  const stockByKey = useMemo(() => {
    const map = {};
    stockRows.forEach((r) => { map[rowKey(r)] = r; });
    return map;
  }, [stockRows]);

  const filteredStockRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return stockRows;
    return stockRows.filter((r) => (r.uniform_item_name || '').toLowerCase().includes(term));
  }, [stockRows, search]);

  const updateQty = (key, val) => {
    setQtyMap((prev) => ({ ...prev, [key]: val ?? 0 }));
    setRowErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  /** Keys of the rows actually being transferred: those with a quantity typed in.
   *  This is the single source of truth for validation and for the payload. */
  const enteredKeys = useMemo(
    () => Object.keys(qtyMap).filter((k) => (qtyMap[k] ?? 0) > 0),
    [qtyMap]
  );

  /** Fill every row currently visible in the grid with its own available quantity.
   *  Scoped to the filtered rows so the action matches what the user can see —
   *  searching first is how you narrow it. */
  const fillVisibleWithMax = () => {
    setQtyMap((prev) => {
      const next = { ...prev };
      filteredStockRows.forEach((row) => { next[rowKey(row)] = row.quantity; });
      return next;
    });
    setRowErrors({});
  };

  const clearQuantities = () => {
    setQtyMap({});
    setRowErrors({});
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  // What "dirty" means for this form, field by field:
  //   transferDate / notes / poNumber / poDate / poDoc — typed or uploaded by
  //     the user, and unrecoverable once the slide-over closes.
  //   enteredKeys — the rows carrying a transfer quantity. This is already the
  //     single source of truth for validation and for the payload, and it only
  //     ever grows from updateQty / fillVisibleWithMax, both user actions. It
  //     is NOT preset on open (the reset effect clears qtyMap to {}).
  //   from / to — deliberately excluded. Both are auto-selected by the two
  //     preselect effects above whenever there is exactly one option, so
  //     treating them as dirty would prompt on a form the user never touched,
  //     in the single-main-warehouse company that is the common case. A
  //     manually picked warehouse is one click to redo and nothing is typed
  //     into it, so it is not work to lose.
  //   stockRows / stockLoading / rowErrors — fetched or server-derived.
  //   search — a view filter over the grid, not part of the transfer.
  const isDirty = enteredKeys.length > 0
    || !!transferDate
    || !!notes.trim()
    || !!poNumber.trim()
    || !!poDate
    || !!poDoc;

  // Every term above is a value comparison against this create-only form's
  // empty baseline — including `poDoc`, which returns to null when an upload is
  // removed — so there is no touch flag here to convert.
  //
  // `requestClose` drives the panel's X button and Esc, so those go through the
  // same prompt a sidebar click does instead of discarding silently.
  const { requestClose } = useUnsavedChangesGuard({
    active: open,
    isDirty,
    isBusy: submitting,
    onDiscard: handleClose,
    label: 'New Stock Transfer',
  });

  const validate = () => {
    if (mainWarehouseOptions.length === 0 && !warehousesLoading) {
      toast.error(
        warehousesError ? "Couldn't load warehouses — retry" : 'No main warehouse configured — flag one under Masters → Warehouses',
        toastOpts
      );
      return false;
    }
    if (!from) { toast.error('Source warehouse is required', toastOpts); return false; }
    if (!to) { toast.error('Destination warehouse is required', toastOpts); return false; }
    if (from.value === to.value) { toast.error('Source and destination must differ', toastOpts); return false; }
    if (!transferDate) { toast.error('Transfer date is required', toastOpts); return false; }
    if (enteredKeys.length === 0) { toast.error('Enter a transfer quantity on at least one row', toastOpts); return false; }
    for (const key of enteredKeys) {
      const row = stockByKey[key];
      const qty = qtyMap[key] ?? 0;
      // A row whose stock vanished between load and submit has no `row` here; the
      // server would reject it anyway, but failing early names the problem.
      if (!row) { toast.error('A row is no longer in stock — refresh the source location', toastOpts); return false; }
      if (qty > row.quantity) { toast.error('One or more rows exceed available stock', toastOpts); return false; }
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const lines = enteredKeys.map((key) => {
      const row = stockByKey[key];
      return { uniform_item: row.uniform_item, size: row.size ?? null, quantity: qtyMap[key] };
    });

    setSubmitting(true);
    createStockTransferApi({
      source_warehouse: from.value,
      destination_warehouse: to.value,
      transfer_date: transferDate,
      notes: notes || '',
      po_number: poNumber || '',
      po_date: poDate || null,
      po_document_url: poDoc?.document_url || '',
      po_document_name: poDoc?.document_name || '',
      lines,
    }).then((response) => {
      setSubmitting(false);
      const reference = response.data?.data?.reference;
      toast.success(reference ? `Transfer ${reference} created` : 'Transfer created', toastOpts);
      onCreated();
    }).catch((err) => {
      setSubmitting(false);
      const serverRows = err.response?.data?.errors;
      if (Array.isArray(serverRows) && serverRows.length) {
        const nextErrors = {};
        serverRows.forEach((row) => {
          const key = `${row.uniform_item}-${row.size ?? 'none'}`;
          nextErrors[key] = row.error || `Only ${row.available ?? 0} available`;
        });
        setRowErrors(nextErrors);

        // Refresh availability, then drop any entered row that no longer comes back
        // (e.g. it hit exactly 0 and fell out of the in_stock=true filter). Without
        // this its quantity would linger with no row left to edit, and every later
        // submit would fail on something the user cannot see.
        fetchStock().then((rows) => {
          const availableKeys = new Set(rows.map(rowKey));
          const currentKeys = Object.keys(qtyMapRef.current).filter(
            (k) => (qtyMapRef.current[k] ?? 0) > 0
          );
          const dropped = currentKeys.filter((k) => !availableKeys.has(k));
          if (dropped.length === 0) return;

          setQtyMap((prev) => {
            const next = { ...prev };
            dropped.forEach((k) => delete next[k]);
            return next;
          });
          setRowErrors((prev) => {
            const next = { ...prev };
            dropped.forEach((k) => delete next[k]);
            return next;
          });
          toast.error(
            `${dropped.length} selected item(s) are no longer in stock and were removed from this transfer`,
            toastOpts
          );
        });
      }
      toast.error(uniformErrorMessage(err, 'Failed to create transfer'), toastOpts);
    });
  };

  const pinLeft = useStickyIdentity();

  const gridColumns = [
    // Pinned: Transfer Qty is typed at the right edge of a 640px grid in a
    // slide-over, and this is the cell that names the row it belongs to.
    { title: 'Uniform', dataIndex: 'uniform_item_name', ...pinLeft(240, { maxWidth: 200 }), ellipsis: true },
    // Plain text here rather than a picker (the size is fixed by the stock row),
    // so this column was never the one at risk — it still gets a floor so a
    // long code cannot be clipped to nothing.
    { title: 'Size', render: (_, r) => r.size_code || '—', width: 110 },
    { title: 'Available Qty', dataIndex: 'quantity', width: 120 },
    {
      title: 'Transfer Qty',
      width: 170,
      render: (_, record) => {
        const key = rowKey(record);
        const qty = qtyMap[key] ?? 0;
        const exceeds = qty > record.quantity;
        return (
          <div>
            <div className="d-flex align-items-center gap-2">
              <InputNumber min={0} max={record.quantity}
                value={qty > 0 ? qty : null} placeholder="0"
                onChange={(val) => updateQty(key, val)}
                // flex:1 rather than a fixed 90px: the "Max" link sits beside it
                // in the same flex row, so the stepper takes whatever the column
                // leaves instead of forcing the pair wider than the column.
                style={{ flex: 1, minWidth: 70 }} />
              <button type="button" className="btn btn-link p-0"
                title={`Transfer all ${record.quantity} available`}
                onClick={() => updateQty(key, record.quantity)}>Max</button>
            </div>
            {exceeds && <small className="text-danger d-block">Exceeds available</small>}
            {rowErrors[key] && <small className="text-danger d-block">{rowErrors[key]}</small>}
          </div>
        );
      }
    },
  ];

  const submitDisabled = submitting || !from || !to || (from && to && from.value === to.value) || enteredKeys.length === 0;

  return (
    <EssFullWidthModal mask={false} sidebarNavigable title="New Stock Transfer" open={open}
      onCancel={requestClose} className="full-width-modal">
      <div className="modal-content">
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-12 col-sm-6 col-xl-3">
                <div className="input-block mb-3">
                  <label className="col-form-label">From Location <span className="text-danger">*</span></label>
                  {mainWarehouseOptions.length === 0 && !warehousesLoading ? (
                    <div className="text-danger" style={{ fontSize: 12.5, padding: '8px 0' }}>
                      {warehousesError ? (
                        <>
                          Couldn't load warehouses —{' '}
                          <button type="button" className="btn btn-link p-0 align-baseline" onClick={fetchWarehouses}>
                            retry
                          </button>.
                        </>
                      ) : (
                        'No main warehouse configured — flag one under Masters → Warehouses.'
                      )}
                    </div>
                  ) : (
                    // Not isClearable: with exactly one option the reconcile effect
                    // above re-selects it on the very next render, so the X would
                    // wipe stockRows/qtyMap via handleFromChange only to have the
                    // same warehouse silently reappear — actively harmful, since the
                    // user's typed quantities are gone but nothing looked cleared.
                    <FilterSelect onChange={handleFromChange} optionLabel="label" optionValue="value"
                      options={mainWarehouseOptions} value={from} placeholder="Select Source" isLoading={warehousesLoading} />
                  )}
                </div>
              </div>
              <div className="col-12 col-sm-6 col-xl-3">
                <div className="input-block mb-3">
                  <label className="col-form-label">To Location <span className="text-danger">*</span></label>
                  {/* Clearing is pointless when the preselect effect would
                      immediately re-select the only option, so the X is offered
                      only when there is a real alternative to switch to. */}
                  <FilterSelect isClearable={toOptions.length > 1} onChange={setTo}
                    optionLabel="label" optionValue="value"
                    options={toOptions} value={to} placeholder="Select Destination"
                    isLoading={warehousesLoading} isDisabled={!from} />
                </div>
              </div>
              <div className="col-12 col-sm-6 col-xl-3">
                <div className="input-block mb-3">
                  <label className="col-form-label" htmlFor="transfer-date">Transfer Date <span className="text-danger">*</span></label>
                  <DateField id="transfer-date" value={transferDate} onChange={setTransferDate}
                    placeholder="Select transfer date" />
                </div>
              </div>
              <div className="col-12 col-sm-6 col-xl-3">
                <div className="input-block mb-3">
                  <label className="col-form-label">Notes</label>
                  <input className="form-control" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes" />
                </div>
              </div>
            </div>

            <Collapse className="mb-3" items={[{
              key: 'po',
              label: 'PO / Reference (optional)',
              children: (
                <div className="row">
                  <div className="col-12 col-sm-6 col-xl-3">
                    <div className="input-block mb-3">
                      <label className="col-form-label">PO Number</label>
                      <input className="form-control" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} placeholder="PO number" />
                    </div>
                  </div>
                  <div className="col-12 col-sm-6 col-xl-3">
                    <div className="input-block mb-3">
                      <label className="col-form-label" htmlFor="transfer-po-date">PO Date</label>
                      <DateField id="transfer-po-date" value={poDate} onChange={setPoDate}
                        placeholder="Select PO date" />
                    </div>
                  </div>
                  <div className="col-12 col-xl-6">
                    <DocumentAttachment label="PO / Reference Document" value={poDoc} onChange={setPoDoc} />
                  </div>
                </div>
              ),
            }]} />

            <hr />
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
              <h5 className="mb-0" style={{ fontSize: 15 }}>Available Stock {from ? `— ${from.label}` : ''}</h5>
              <div className="d-flex align-items-center flex-wrap gap-2">
                <Button type="button" label="Transfer all available" icon="fa fa-angles-right"
                  className="btn btn-outline-primary btn-sm"
                  disabled={!from || filteredStockRows.length === 0}
                  title={`Set the ${filteredStockRows.length} row(s) shown to their available quantity`}
                  onClick={fillVisibleWithMax} />
                <Button type="button" label="Clear" icon="fa fa-eraser"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={enteredKeys.length === 0}
                  title="Clear every quantity entered"
                  onClick={clearQuantities} />
                {/* `flex: 1 1 170px`, not the old fixed 240: as a flex item a
                    width of 100% would claim the whole strip and always wrap to
                    its own line, while a fixed 240 made the search plus two
                    buttons one run too wide to fit. This shrinks to 170 first
                    and only wraps when even that will not fit. */}
                <input className="form-control" style={{ flex: '1 1 170px', minWidth: 170, maxWidth: 240 }} placeholder="Search uniform..."
                  value={search} onChange={(e) => setSearch(e.target.value)} disabled={!from} />
              </div>
            </div>

            {!from ? (
              <div className="text-muted py-4 text-center">Select a source location to see its available stock.</div>
            ) : (
              <Table
                rowKey={rowKey}
                columns={gridColumns}
                dataSource={filteredStockRows}
                loading={stockLoading}
                pagination={false}
                size="small"
                // Already fixed-layout before this change, via `scroll.y`
                // (rc-table reads it as `fixHeader`, es/Table.js:453) and now
                // also via the `ellipsis` on Uniform; the missing widths were
                // the whole bug. Declared anyway so the widths cannot silently
                // become advisory if the sticky header is ever removed.
                tableLayout="fixed"
                // 640 is the sum of the four column widths, so all of them
                // including Size fit without scrolling from about 900px of
                // panel width up.
                scroll={{ x: 640, y: 360 }}
                locale={{ emptyText: 'No in-stock items at this location' }}
              />
            )}

            <div className="submit-section">
              <Button loading={submitting} disabled={submitDisabled} className="btn btn-primary submit-btn"
                type="submit" label="Create Transfer" />
            </div>
          </form>
        </div>
      </div>
    </EssFullWidthModal>
  );
};

export default TransferFormModal;
